<template>
  <div ref="editorParent" class="flex h-full flex-col">
    <div class="border-b border-gray-300/75 py-2">
      <div class="flex items-start justify-between gap-1 pr-4">
        <div class="flex flex-col">
          <div class="relative">
            <EntryTitle
              class="grow"
              :largeTitle="largeTitle"
              :title="entry.title"
              :entry="entry"
              :isMainContent="isMainContent"
              @save="saveTitle"
              @toggle="titleInputActive = $event"
            />
          </div>
          <div class="nowrap flex items-center gap-6">
            <SubHeader
              v-if="entry.legal_text_paragraph"
              class="shrink-0"
              :paragraph="entry.legal_text_paragraph"
            />
            <div
              v-if="
                (entry?.from_channels && entry?.from_channels.length > 0) ||
                (entry?.channels && entry?.channels.length > 0)
              "
              class="flex w-full items-center gap-1 text-xs"
            >
              <BroadcastIcon
                class="shrink-0 scale-75 cursor-default !p-0 hover:bg-transparent"
              ></BroadcastIcon>
              <span
                v-if="channelsList"
                class="w-[calc(100%_-_4.5rem)] truncate"
              >
                {{ channelsList }}
              </span>
            </div>
          </div>
        </div>
        <button
          v-if="!titleInputActive"
          v-tooltip.bottom="t('close_document')"
          class="shrink-0 grow-0 rounded-full p-2 transition hover:bg-gray-200"
          @click="emit('close')"
        >
          <span class="sr-only">{{ t("close_document") }}</span>
          <i-heroicons-x-mark-20-solid class="size-5" aria-hidden="true" />
        </button>
      </div>

      <div class="flex items-end">
        <FolBreadcrumb
          v-if="fieldOfLaw"
          class="w-[calc(100%_-_7.5rem)] flex-1"
          :fieldOfLaw="fieldOfLaw"
        />
        <div class="ml-auto flex shrink-0 items-center justify-center gap-1">
          <button
            v-if="isOwned"
            v-tooltip.bottom="
              editMode
                ? t('legal_text.textEditor.tooltips.stop_editing')
                : t('legal_text.textEditor.tooltips.edit_schema')
            "
            class="ml-2 rounded p-1 transition md:hover:bg-gray-200"
            :class="{
              'bg-lexmea-selection-lightest md:hover:!bg-lexmea-selection-lighter':
                editMode,
            }"
            :aria-pressed="editMode"
            @click="editMode = !editMode"
          >
            <span class="sr-only">
              {{
                editMode
                  ? t("legal_text.textEditor.tooltips.stop_editing")
                  : t("legal_text.textEditor.tooltips.edit_schema")
              }}
            </span>
            <SchemaEditIcon class="h-5 w-5" alt="edit" aria-hidden="true" />
          </button>
          <ShareSchemaIconButton
            v-if="canCreateChannels && isOwned"
            :schema="entry"
          />
          <SchemaPdfButtonWithLazyModal
            class="rounded p-1"
            :isCurated
            :isOwned
            :isDownloadable
            :preselectedIds="[entry.id]"
          />
          <button
            v-if="isOwned"
            v-tooltip.bottom="
              entry.editable
                ? t('legal_text.textEditor.tooltips.delete')
                : t(
                    'legal_text.textEditor.tooltips.only_self_created_can_be_deleted'
                  )
            "
            :disabled="!entry.editable"
            class="rounded p-1"
            :class="{ 'transition hover:bg-gray-200': entry.editable }"
            @click="deleteSchema(entry)"
          >
            <span class="sr-only">
              {{
                entry.editable
                  ? t("legal_text.textEditor.tooltips.delete")
                  : t(
                      "legal_text.textEditor.tooltips.only_self_created_can_be_deleted"
                    )
              }}
            </span>
            <i-heroicons-trash
              class="h-5 w-5"
              :class="{ 'text-gray-400/60': !entry.editable }"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>
    <Transition name="scale">
      <div
        v-show="editMode"
        id="editor_toolbar"
        class="z-20 w-full origin-top bg-white max-lg:sticky max-lg:inset-x-0 max-lg:top-12 lg:max-w-max"
      ></div>
    </Transition>
    <div
      :class="[
        editMode && !isSchemaEditorOnFocus
          ? 'border-gray-300/75'
          : 'border-transparent',
      ]"
      class="mb-1 border-b pb-0.5"
    ></div>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div v-if="!clientRendering" v-html="editorContent"></div>
    <SchemaEditor
      v-else
      v-model="editorContent"
      v-model:editorRef="editorRef"
      :onInit="onInit"
      :disabled="editorDisabled"
      @focusin="handleSchemaEditorFocus"
      @focusout="handleSchemaEditorFocus"
    />
    <BottomToolbar
      :class="{ 'border-t border-gray-300/75': !isSchemaEditorOnFocus }"
      :editing="editMode"
      :entry="entry"
      :publishable="curatedAndAdmin"
      :saving="saving"
      @publish="handlePublish"
    />
  </div>
</template>

<script lang="ts" setup>
import type { IFieldOfLawWithParents } from "~/models/IFieldOfLaw";
import type { ISchemaWithText } from "~/models/IUserEntry";

type Props = {
  entry: ISchemaWithText;
  fieldOfLaw?: IFieldOfLawWithParents;
  isMainContent: boolean;
  largeTitle?: boolean;
};

type Emits = {
  close: [];
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();

const isSchemaEditorOnFocus = ref(false);

function handleSchemaEditorFocus(event: FocusEvent) {
  if (event.type === "focusin") {
    isSchemaEditorOnFocus.value = true;
  } else if (event.type === "focusout") {
    isSchemaEditorOnFocus.value = false;
  }
}

const entry = toRef(props, "entry");

const { open: deleteSchema } = useEntryDeleteModal();

const clientRendering = useMounted();

const titleInputActive = ref(false);

const { editMode } = storeToRefs(useEditorStore());
const {
  editorContent,
  curatedAndAdmin,
  editorDisabled,
  editorRef,
  editorParent,
  isDownloadable,
  isCurated,
  isOwned,
  onInit,
} = useSchemaEditor(entry, editMode);

const { handleSave, updatingEntry } = useSaveEntry(entry);

const { togglePublishedMutation } = schemaMutations();

const handlePublish = (publish: boolean) => {
  togglePublishedMutation.mutate({
    id: entry.value.id,
    publish,
  });
};

const saving = logicOr(updatingEntry, togglePublishedMutation.isPending);

const saveTitle = useDebounceFn((title: string) => handleSave({ title }), 200);

const autosave = useDebounceFn(() => {
  if (entry.value.editable) {
    handleSave({ text: editorContent.value });
  }
}, 500);

watch(
  [entry, editorContent],
  ([newEntry, newContent], [oldEntry, oldContent]) => {
    // don't do anything when we switch the entire schema
    if (newEntry.id !== oldEntry.id) {
      return;
    }
    if (newContent !== oldContent) {
      return autosave();
    }
  }
);

onUnmounted(() => {
  editMode.value = false;
});

const canCreateChannels = useCanCreateChannels();

const channelsList = computed(() => {
  const channels = entry.value?.channels;
  const fromChannels = entry.value?.from_channels;

  if (isOwned.value && channels && channels.length > 0) {
    return t("shared_in", [
      channels.map((channel) => channel.name).join(" | "),
    ]);
  } else if (fromChannels && fromChannels.length > 0) {
    return `${fromChannels[0].owner} | ${fromChannels.map((channel) => channel.name).join(" | ")}`;
  }

  return false;
});
</script>

<style lang="postcss">
.tox-tinymce {
  @apply border-none !important;
}

.tox-toolbar {
  @apply bg-inherit bg-none !important;

  &-overlord {
    @apply bg-inherit !important;
  }
}

.tox-editor-header {
  @apply bg-inherit !important;
}

/* stylelint-disable-next-line selector-class-pattern */
.tox-tbtn--disabled {
  pointer-events: none;
}

.mce-content-body [contentEditable="false"][data-mce-selected] {
  cursor: pointer !important;
}

#tableOfContents {
  cursor: pointer;

  li:hover {
    @apply text-lexmea-pop-lighter;
  }
}
</style>

<i18n>
{
  "de": {
    "shared_in": "Geteilt in: {0}",
    "close_document": "@:entries.schema schließen"
  },
  "en": {
    "shared_in": "Shared in: {0}",
    "close_document": "Close @:entries.schema"
  }
}
</i18n>
